/**
 * Canonical chart-granularity bucketing semantics, shared by the client
 * chart pipeline (`@tokenterminal/core` — `useSeries` + the tt-ui-chart
 * render layer) and the API's composed chart endpoint
 * (`metrics.getSectorChartData`).
 *
 * This is THE single implementation — the client utils in
 * `libs/ui/src/feature/chart/tt-ui-chart/utils/` re-export from here, and
 * the server imports it directly. Do not fork or hand-copy these functions:
 * chart correctness depends on the server's pre-aggregated buckets and the
 * client's (re-)grouping using identical anchors and aggregation rules.
 *
 * Semantics:
 *   - Weeks start on MONDAY 00:00 UTC (Sunday rolls back to the previous
 *     Monday).
 *   - Months snap to the 1st UTC; quarters to Jan/Apr/Jul/Oct 1st UTC
 *     (a quarter is a `{ name: 'month', count: 3 }` unit).
 *   - Cumulative metrics aggregate each bucket by SUM; static metrics by
 *     AVERAGE over a shared per-period denominator (the count of distinct
 *     daily timestamps present anywhere in the chart's result), so every
 *     series divides by the same N and groupings reconcile to the total.
 *   - A bucket whose values are all null stays null.
 */

export type GranularityType = "day" | "week" | "month" | "quarter";
export type ChartMetricApproximation = "average" | "sum";

export type GranularityInfo = {
  approximation?: ChartMetricApproximation;
  unit: {
    name: "day" | "week" | "month";
    count: number;
  };
};

/** `[timestampMs, value]` — the chart pipeline's time-series point shape
 * (`ChartSerieTimeData` on the client). */
export type TimeSeriesPoint = [number, number | null];

export function calculateGranularityUnit(
  granularity: GranularityType,
): GranularityInfo["unit"] {
  switch (granularity) {
    case "quarter": {
      return {
        name: "month",
        count: 3,
      };
    }
    default:
      return {
        name: granularity,
        count: 1,
      };
  }
}

export function getStartOfPeriod(
  date: Date | number,
  period: GranularityType,
  count: number,
): number {
  const parsedDate = new Date(date);
  switch (period) {
    // Day buckets: the period start is simply the start of that UTC day.
    // (Callers currently skip grouping for day granularity, but a shared
    // module must not throw on a valid GranularityType.)
    case "day": {
      break;
    }
    case "week": {
      const day = parsedDate.getUTCDay();
      const diff = parsedDate.getUTCDate() - day + (day === 0 ? -6 : 1); // adjust when day is sunday
      parsedDate.setUTCDate(diff + 7 * (count - 1));
      break;
    }
    case "month": {
      parsedDate.setUTCDate(1);
      const month = parsedDate.getUTCMonth();
      parsedDate.setUTCMonth(month - (month % count), 1);

      break;
    }
    default:
      throw new Error("Invalid period. Choose from 'day', 'week', 'month'.");
  }

  parsedDate.setUTCHours(0, 0, 0, 0);
  return parsedDate.getTime();
}

/**
 * Computes a per-period denominator (count of distinct daily timestamps) from a
 * pool of timestamps spanning multiple series. Used by `groupDataBasedOnGranularity`
 * so that every series in the same chart divides averages by the same N for a
 * given period — preventing inflation when a series has data for fewer days than
 * the period contains (e.g., a chain that launched mid-month).
 */
export function computePeriodDenominators(
  timestamps: Iterable<number>,
  unit: GranularityInfo["unit"],
): Map<number, number> {
  const perPeriodDays = new Map<number, Set<number>>();
  for (const ts of timestamps) {
    const periodStart = getStartOfPeriod(ts, unit.name, unit.count);
    let days = perPeriodDays.get(periodStart);
    if (!days) {
      days = new Set<number>();
      perPeriodDays.set(periodStart, days);
    }
    days.add(ts);
  }
  const result = new Map<number, number>();
  for (const [period, days] of perPeriodDays) {
    result.set(period, days.size);
  }
  return result;
}

/**
 * Bucket daily points into calendar periods. `zoom` is the resolved day
 * count for the interval (`convertApiToZoom` on the client; the API's
 * interval→days mapping on the server), with the enum values 0 = All Time,
 * -1 = YTD, -2 = four full quarters passing the data through unsliced.
 */
export function groupDataBasedOnGranularity(
  serieData: TimeSeriesPoint[],
  { approximation = "sum", unit }: GranularityInfo,
  zoom: number,
  periodDenominators?: Map<number, number>,
): TimeSeriesPoint[] {
  // All sentinel zooms (0 = All Time, -1 = YTD, -2 = four full quarters)
  // pass the data through unsliced; positive day counts slice the tail.
  // (`zoom === -1` alone made -2 behave as `slice(2)`, silently dropping
  // the two OLDEST points.)
  const serieDataForPeriod = zoom <= 0 ? serieData : serieData.slice(-1 * zoom);

  const firstPoint = serieDataForPeriod[0];
  if (!firstPoint) {
    return serieDataForPeriod;
  }

  const startOfPeriodBasedOnZoom = getStartOfPeriod(
    firstPoint[0],
    unit.name,
    unit.count,
  );

  // remove all data that is not in the current period
  const filteredData = serieData.filter(
    ([timestamp]) => timestamp >= startOfPeriodBasedOnZoom,
  );

  // Group the data by the start of the period
  const groupedData = new Map<number, Array<number | null>>();
  // a helper to keep track of null values so we don't count them for average
  const groupedNullData = new Map<number, number>();
  for (const row of filteredData) {
    const startOfPeriod = getStartOfPeriod(row[0], unit.name, unit.count);
    const periodData = groupedData.get(startOfPeriod) ?? [];
    let nullCount = groupedNullData.get(startOfPeriod) ?? 0;

    periodData.push(row[1]);

    if (row[1] === null) {
      nullCount++;
    }

    groupedData.set(startOfPeriod, periodData);
    groupedNullData.set(startOfPeriod, nullCount);
  }

  const result: TimeSeriesPoint[] = [];
  for (const [key, values] of groupedData) {
    const sum = values.reduce((prev, curr) => {
      if (prev === null) {
        return 0;
      }

      if (curr === null) {
        return prev;
      }

      return curr + prev;
    }, 0);

    // if we only have null values, we should return null
    if (groupedNullData.get(key) === values.length) {
      result.push([key, null]);
    } else {
      // Prefer a shared per-period denominator so all series in the same chart
      // average over the same N (e.g., 30 for September). Fallback to this
      // series' own non-null count for backwards compatibility.
      const sharedDenominator = periodDenominators?.get(key);
      const denominator =
        sharedDenominator && sharedDenominator > 0
          ? sharedDenominator
          : values.length - (groupedNullData.get(key) ?? 0);
      const average = (sum as number) / denominator;

      result.push([key, approximation === "sum" ? sum : average]);
    }
  }

  return result;
}

/**
 * Checks if a timestamp falls on the first day of its granularity period.
 * For months: checks if it's the 1st of the month
 * For quarters: checks if it's the 1st of a quarter month (Jan 1, Apr 1, Jul 1, Oct 1)
 */
function isFirstDayOfPeriod(
  timestamp: number,
  granularity: GranularityType,
): boolean {
  const date = new Date(timestamp);

  if (granularity === "month") {
    return date.getUTCDate() === 1;
  }

  if (granularity === "quarter") {
    const quarterStartMonths = [0, 3, 6, 9]; // Jan, Apr, Jul, Oct
    return (
      date.getUTCDate() === 1 && quarterStartMonths.includes(date.getUTCMonth())
    );
  }

  return false;
}

/**
 * Decides whether to keep the earliest bucket even when `detectPartialPeriods`
 * has flagged it partial. We override the filter in cases where dropping the
 * lead-in would hide useful data:
 *
 * 1. **All Time (`max`)**: always keep -- the earliest bucket represents the
 *    true start of the metric's history.
 *
 * 2. **Calendar-aligned zooms** where the interval starts on a period boundary:
 *    - `ytd` + month/quarter: YTD starts Jan 1, aligned with both. (Not week --
 *      Jan 1 rarely falls on a Monday.)
 *    - `4fullq` + month/quarter: starts at a quarter boundary (Jan/Apr/Jul/Oct
 *      1st), aligned with both. (Not week, same reason.)
 *
 *    For these we *also* check that the data actually starts on the period's
 *    first day. A metric that begins mid-month doesn't get a free pass just
 *    because YTD is selected.
 *
 * 3. **Day-based zoom wider than the metric's history**: e.g., a 3y zoom on a
 *    2-week-old metric. The earliest data point falls *inside* the requested
 *    window, so the user is effectively asking for All Time -- preserve the
 *    bucket. Without this, brand-new metrics would show only their second week
 *    onward at long zooms.
 *
 * @param zoom - The interval/zoom level string (e.g., "ytd", "4fullq", "max", "1095d")
 * @param zoomDays - The interval resolved to a day count (`convertApiToZoom`
 *   on the client; positive for day-based zooms, <= 0 for the enum zooms)
 * @param granularity - The granularity (e.g., "month", "quarter", "week")
 * @param earliestDataTimestamp - Timestamp of the earliest data point in the series
 */
export function shouldPreserveEarliestPeriod(
  zoom: string,
  zoomDays: number,
  granularity: GranularityType,
  earliestDataTimestamp?: number,
): boolean {
  if (zoom === "max") {
    return true;
  }

  if (
    zoom === "ytd" &&
    (granularity === "month" || granularity === "quarter")
  ) {
    if (earliestDataTimestamp !== undefined) {
      return isFirstDayOfPeriod(earliestDataTimestamp, granularity);
    }
    return true;
  }

  if (
    zoom === "4fullq" &&
    (granularity === "quarter" || granularity === "month")
  ) {
    if (earliestDataTimestamp !== undefined) {
      return isFirstDayOfPeriod(earliestDataTimestamp, granularity);
    }
    return true;
  }

  // Day-based zoom wider than the metric's history: `zoomDays` is a positive
  // day count for these and a non-positive enum value otherwise.
  if (zoomDays > 0 && earliestDataTimestamp !== undefined) {
    const windowStart = Date.now() - zoomDays * 24 * 60 * 60 * 1000;
    if (earliestDataTimestamp >= windowStart) {
      return true;
    }
  }

  return false;
}

/**
 * Drops the partial edge buckets flagged by `detectPartialPeriods` from a
 * grouped series — always the latest (current) partial period, and the
 * earliest one unless `shouldPreserveEarliestPeriod` decides the
 * zoom/granularity combination naturally aligns with calendar boundaries.
 *
 * Callers own the "should we filter at all" guard (`granularity === "day"`
 * or `showCurrentPeriod` render both edges as-is).
 */
export function filterPartialPeriods(
  data: TimeSeriesPoint[],
  partialPeriods: {
    earliestPartialPeriod: number | null;
    latestPartialPeriod: number | null;
    earliestTimestamp: number | null;
  },
  zoom: string,
  zoomDays: number,
  granularity: GranularityType,
): TimeSeriesPoint[] {
  const { earliestPartialPeriod, latestPartialPeriod, earliestTimestamp } =
    partialPeriods;
  return data.filter(([timestamp]) => {
    // Always filter out the latest (current) partial period.
    if (latestPartialPeriod !== null && timestamp === latestPartialPeriod) {
      return false;
    }
    // Only filter out the earliest partial period if the zoom/granularity
    // combination doesn't naturally align with calendar boundaries.
    if (
      earliestPartialPeriod !== null &&
      timestamp === earliestPartialPeriod &&
      !shouldPreserveEarliestPeriod(
        zoom,
        zoomDays,
        granularity,
        earliestTimestamp ?? undefined,
      )
    ) {
      return false;
    }
    return true;
  });
}

/**
 * Detects whether the buckets at the start and end of the data are partial
 * (i.e., calendar periods only fractionally covered by the data).
 *
 * Conceptual model
 * ----------------
 * Daily data is bucketed into calendar periods (Mon-Sun for week, 1st-last for
 * month, etc.). A bucket is **partial** when the data doesn't cover the full
 * calendar period:
 *
 *   - Earliest bucket: we have data going forward from `earliestDate`, but
 *     nothing before it. The bucket is partial unless data covers the period
 *     from its first day -- i.e., `earliestDate` lands on the period START
 *     (Monday for week, 1st for month, first day of quarter).
 *
 *   - Latest bucket: we have data going backward from `latestDate`, but
 *     nothing after. The bucket is partial unless data extends to the period's
 *     last day -- i.e., `latestDate` lands on the period END (Sunday for week,
 *     last-of-month, last day of quarter).
 *
 * The two checks are mirror images: one tests period start, the other tests
 * period end. Mid-period dates are partial on either edge.
 *
 * Why callers care
 * ----------------
 * When `showCurrentPeriod` is false (the default for cumulative metrics), the
 * caller filters out partial buckets so a fractional week/month doesn't appear
 * as a misleadingly small bar. `shouldPreserveEarliestPeriod` can override the
 * earliest-side filter for cases like All Time or "data is younger than the
 * requested zoom window" where dropping the lead-in is undesirable.
 *
 * Returns null for either side when not partial. Returns the bucket's
 * period-start timestamp when partial, so the caller can match it against
 * the post-grouping bucket keys (which are also period-start timestamps).
 *
 * Also returns the raw earliest timestamp for use by
 * `shouldPreserveEarliestPeriod`, so callers don't need to reach back into
 * the input array themselves.
 *
 * Note: `data` is sorted descending -- data[0] is most recent,
 * data.at(-1) is oldest. Only the two endpoints are read, so callers that
 * know their earliest/latest timestamps can pass a synthetic two-point
 * array `[[latestTs, null], [earliestTs, null]]`.
 */
export function detectPartialPeriods(
  data: TimeSeriesPoint[],
  granularity: GranularityType,
): {
  earliestPartialPeriod: number | null;
  latestPartialPeriod: number | null;
  earliestTimestamp: number | null;
} {
  const result = {
    earliestPartialPeriod: null as number | null,
    latestPartialPeriod: null as number | null,
    // (`data[data.length - 1]` rather than `data.at(-1)` — this package
    // targets es2021.)
    earliestTimestamp: data[data.length - 1]?.[0] ?? null,
  };

  if (data.length === 0 || granularity === "day") {
    return result;
  }

  const latestDataPoint = data[0];
  if (!latestDataPoint) {
    return result;
  }
  const latestTimestamp = latestDataPoint[0];
  const latestDate = new Date(latestTimestamp);

  let latestPeriodStart: number;
  let isLatestPartial = false;

  switch (granularity) {
    case "week": {
      // Get the Monday of this week
      const day = latestDate.getUTCDay();
      const diff = latestDate.getUTCDate() - day + (day === 0 ? -6 : 1);
      const monday = new Date(latestDate);
      monday.setUTCDate(diff);
      monday.setUTCHours(0, 0, 0, 0);
      latestPeriodStart = monday.getTime();
      // The latest period is complete only if data extends to its last day
      // (Sunday). Otherwise the period is partial.
      isLatestPartial = day !== 0;
      break;
    }
    case "month": {
      // Get the first day of this month
      const monthStart = new Date(latestDate);
      monthStart.setUTCDate(1);
      monthStart.setUTCHours(0, 0, 0, 0);
      latestPeriodStart = monthStart.getTime();
      const lastDayOfMonth = new Date(
        Date.UTC(latestDate.getUTCFullYear(), latestDate.getUTCMonth() + 1, 0),
      ).getUTCDate();
      // Partial unless data extends to the last day of the month
      isLatestPartial = latestDate.getUTCDate() !== lastDayOfMonth;
      break;
    }
    case "quarter": {
      // Get the first day of this quarter
      const quarterMonth = Math.floor(latestDate.getUTCMonth() / 3) * 3;
      const quarterStart = new Date(latestDate);
      quarterStart.setUTCMonth(quarterMonth, 1);
      quarterStart.setUTCHours(0, 0, 0, 0);
      latestPeriodStart = quarterStart.getTime();
      const lastMonthOfQuarter = quarterMonth + 2;
      const lastDayOfQuarter = new Date(
        Date.UTC(latestDate.getUTCFullYear(), lastMonthOfQuarter + 1, 0),
      );
      // Partial unless data extends to the last day of the quarter
      isLatestPartial = latestDate.getTime() < lastDayOfQuarter.getTime();
      break;
    }
    default:
      return result;
  }

  if (isLatestPartial) {
    result.latestPartialPeriod = latestPeriodStart;
  }

  // Check if the earliest (oldest) data point ends at the end of its period
  const earliestDataPoint = data[data.length - 1];
  if (!earliestDataPoint) {
    return result;
  }
  const earliestTimestamp = earliestDataPoint[0];
  const earliestDate = new Date(earliestTimestamp);
  let earliestPeriodStart: number;
  let isEarliestPartial = false;

  switch (granularity) {
    case "week": {
      // Get the Monday of this week
      const day = earliestDate.getUTCDay();
      const diff = earliestDate.getUTCDate() - day + (day === 0 ? -6 : 1);
      const monday = new Date(earliestDate);
      monday.setUTCDate(diff);
      monday.setUTCHours(0, 0, 0, 0);
      earliestPeriodStart = monday.getTime();
      // The earliest period is complete only if data covers it from its first
      // day (Monday). Otherwise the period is partial.
      isEarliestPartial = day !== 1;
      break;
    }
    case "month": {
      // Get the first day of this month
      const monthStart = new Date(earliestDate);
      monthStart.setUTCDate(1);
      monthStart.setUTCHours(0, 0, 0, 0);
      earliestPeriodStart = monthStart.getTime();
      // Partial unless data covers the period from the 1st of the month
      isEarliestPartial = earliestDate.getUTCDate() !== 1;
      break;
    }
    case "quarter": {
      // Get the first day of this quarter
      const quarterMonth = Math.floor(earliestDate.getUTCMonth() / 3) * 3;
      const quarterStart = new Date(earliestDate);
      quarterStart.setUTCMonth(quarterMonth, 1);
      quarterStart.setUTCHours(0, 0, 0, 0);
      earliestPeriodStart = quarterStart.getTime();
      // Partial unless data covers the period from the first day of the quarter
      isEarliestPartial =
        earliestDate.getUTCMonth() !== quarterMonth ||
        earliestDate.getUTCDate() !== 1;
      break;
    }
    default:
      return result;
  }

  if (isEarliestPartial) {
    result.earliestPartialPeriod = earliestPeriodStart;
  }

  return result;
}
